import React from 'react';

import { Box, Button, Typography } from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false, error: null };
        this.handleGoBack = this.handleGoBack.bind(this);
        this.handleReload = this.handleReload.bind(this);
    }

    static getDerivedStateFromError(error) {
        return { hasError: true, error };
    }

    componentDidCatch(error, info) {
        if (typeof console !== 'undefined') {
            console.error('[ErrorBoundary] caught render error:', error, info);
        }
        if (typeof window !== 'undefined' && window.Sentry?.captureException) {
            try {
                window.Sentry.captureException(error, { extra: { componentStack: info?.componentStack } });
            } catch (_) {
                // Sentry failure must never block the recovery UI.
            }
        }
    }

    handleGoBack() {
        this.setState({ hasError: false, error: null });
        if (typeof window !== 'undefined' && window.history) {
            window.history.back();
        }
    }

    handleReload() {
        if (typeof window !== 'undefined' && window.location) {
            window.location.reload();
        }
    }

    render() {
        if (!this.state.hasError) {
            return this.props.children;
        }

        const errorMessage = this.state.error?.message || '';

        return (
            <Box
                role="alert"
                style={{
                    position: 'fixed',
                    inset: 0,
                    zIndex: 2147482000,
                    background: '#FAFAFA',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: 24,
                    paddingTop: 'calc(env(safe-area-inset-top, 0px) + 24px)',
                    paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 24px)',
                    textAlign: 'center',
                }}
            >
                <Box
                    style={{
                        width: 72,
                        height: 72,
                        borderRadius: 36,
                        background: 'rgba(0, 84, 150, 0.08)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        marginBottom: 20,
                    }}
                >
                    <ErrorOutlineIcon style={{ color: '#005496', fontSize: 36 }} />
                </Box>
                <Typography
                    style={{
                        fontSize: 20,
                        fontWeight: 700,
                        color: '#0E1524',
                        marginBottom: 8,
                    }}
                >
                    Something went wrong / משהו השתבש
                </Typography>
                <Typography
                    style={{
                        fontSize: 14,
                        color: 'rgba(14, 21, 36, 0.65)',
                        marginBottom: 24,
                        maxWidth: 360,
                    }}
                >
                    The screen could not be displayed. Please go back or reload the app.
                    {errorMessage ? ` (${errorMessage})` : ''}
                </Typography>
                <Box style={{ display: 'flex', flexDirection: 'column', gap: 12, width: '100%', maxWidth: 280 }}>
                    <Button
                        variant="contained"
                        onClick={this.handleGoBack}
                        style={{
                            backgroundColor: '#005496',
                            color: '#FFFFFF',
                            borderRadius: 999,
                            minHeight: 48,
                            fontWeight: 600,
                            textTransform: 'none',
                        }}
                    >
                        Go back / חזור
                    </Button>
                    <Button
                        variant="outlined"
                        onClick={this.handleReload}
                        style={{
                            borderColor: '#005496',
                            color: '#005496',
                            borderRadius: 999,
                            minHeight: 48,
                            fontWeight: 600,
                            textTransform: 'none',
                        }}
                    >
                        Reload / טען מחדש
                    </Button>
                </Box>
            </Box>
        );
    }
}

export default ErrorBoundary;
